/**
 * Google's own sign-in button, rendered by Google's script.
 *
 * The button has to come from Google — a lookalike of our own cannot obtain a
 * credential — so this loads their library once, hands it the client id, and
 * passes the resulting ID token up. Everything after that is our own session.
 *
 * It renders nothing at all when Google sign-in is not configured. An offered
 * button that fails on click is worse than an absent one.
 *
 * Note for deployment: `Cross-Origin-Opener-Policy: same-origin` silently
 * breaks this — the popup closes and the credential never arrives. See
 * frontend/security-headers.conf.
 */

import { useEffect, useRef, useState } from "react";

import { api } from "../api/client";

const SCRIPT_SRC = "https://accounts.google.com/gsi/client";

interface Props {
  onCredential: (credential: string) => void;
  /** "signin_with" | "signup_with" — Google renders the wording. */
  text?: "signin_with" | "signup_with" | "continue_with";
  /**
   * Told whether this component is going to draw anything.
   *
   * The caller cannot work it out for itself: whether Google is configured is
   * only known after `/api/auth/config` answers, and the script can still fail
   * afterwards. Without this a surrounding "Or" divider renders above nothing
   * on every deployment where GOOGLE_CLIENT_ID is unset — which is a rule and
   * a caption promising a sign-in option that does not exist.
   */
  onReady?: (enabled: boolean) => void;
}

export function GoogleButton({ onCredential, text = "continue_with",
                                onReady }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const [clientId, setClientId] = useState("");
  const [failed, setFailed] = useState(false);
  // The column this button has to fit. Measured rather than assumed: Google
  // renders its *personalised* variant — "Sign in as …", shown once you are
  // signed into Google — inside a cross-origin iframe, and no stylesheet of
  // ours reaches into that. Whatever pixel width is handed to renderButton is
  // the width it takes, so a hardcoded one overflows the card the moment the
  // card is narrower than it.
  const [column, setColumn] = useState(0);
  // Held in a ref so re-rendering the button never re-registers a stale
  // callback with Google's library.
  const callback = useRef(onCredential);
  callback.current = onCredential;
  // Held in a ref for the same reason, so reporting readiness cannot loop
  // through a caller that passes a fresh closure on every render.
  const ready = useRef(onReady);
  ready.current = onReady;

  // Keyed on clientId, not on mount. The host div only exists once there is a
  // client id — the component renders null before that — so measuring on mount
  // measures nothing, and with an empty dependency list it would never look
  // again: column stays 0, the render effect below bails on it, and the button
  // never appears at all.
  useEffect(() => {
    const element = host.current;
    if (!clientId || !element) return;
    const measure = () =>
      setColumn(Math.round(element.getBoundingClientRect().width));
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [clientId]);

  useEffect(() => {
    const controller = new AbortController();
    api
      .authConfig(controller.signal)
      .then((config) => {
        if (config.google_enabled) setClientId(config.google_client_id);
      })
      .catch(() => {
        /* not configured, or the server is down; the form still works */
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!clientId || !host.current || !column) return;

    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !host.current) return;
        const google = (window as GoogleWindow).google;
        if (!google) return;

        google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => callback.current(response.credential),
        });
        // Google's own options first, CSS only for what they do not expose.
        // `outline` reads as the secondary action it is — the black Sign in
        // button above it is the primary one, and two filled blacks in a
        // column flatten that. `square` is their least rounded shape; the
        // last of the radius and their drop shadow come off in CSS, which is
        // what made the default look borrowed from another site.
        //
        // Emptied first: renderButton appends, so re-rendering on a resize
        // would stack a second button under the first.
        host.current.innerHTML = "";
        // Width is measured, not chosen. Google clamps it to 200–400, so a
        // column outside that range gets the nearest end rather than a button
        // wider than its card. Re-rendered whenever the column changes, which
        // is what makes this survive a resize.
        google.accounts.id.renderButton(host.current, {
          theme: "outline",
          shape: "square",
          size: "large",
          logo_alignment: "center",
          text,
          width: Math.max(200, Math.min(400, column)),
        });
      })
      .catch(() => setFailed(true));

    return () => {
      cancelled = true;
    };
  }, [clientId, text, column]);

  const enabled = Boolean(clientId) && !failed;
  useEffect(() => {
    ready.current?.(enabled);
  }, [enabled]);

  if (!clientId) return null;
  if (failed) {
    return (
      <p className="sub">
        Google sign-in couldn't load. Use your email and password above.
      </p>
    );
  }

  return <div className="google-button" ref={host} />;
}

// ---------------------------------------------------------------------------

interface GoogleWindow extends Window {
  google?: {
    accounts: {
      id: {
        initialize: (options: {
          client_id: string;
          callback: (response: { credential: string }) => void;
        }) => void;
        renderButton: (host: HTMLElement, options: Record<string, unknown>) => void;
      };
    };
  };
}

let loading: Promise<void> | null = null;

/** Loads Google's library once, however many buttons ask for it. */
function loadScript(): Promise<void> {
  if (loading) return loading;

  loading = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("blocked")));
      if ((window as GoogleWindow).google) resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // A blocked script is the normal case behind a content blocker, not an
      // exception worth surfacing as an error banner.
      loading = null;
      reject(new Error("blocked"));
    };
    document.head.appendChild(script);
  });

  return loading;
}
